import { createContext, useState, useCallback, useEffect } from 'react';
import { BasketContextInterface } from '../types/basket.context.types';
import { BasketLineItem, PreviewBasketItem } from '../types/basket.types';
import { roundOffDecimalQuantity } from '../utils/basket.util';
import { useStaticData } from './DataProvider.context';
import SendBrowserEvent from '../services/sendBrowserEvent.service';
import { ANALYTICS } from '../constants/analytics';
import BasketService from '../services/basket/basket.service';
import { getProductIdWithoutPrefix } from '../utils/product.util';
import { COUNTRY } from '@mango-engineering/maknet-client-sdk/lib/shop/types';
import { isAlcoholProhibitedPeriod } from '../utils/common';
import { WEBVIEW_MESSAGE_TYPE } from '../utils/constants';
import { checkPlatform } from '../utils/helper';

const BasketContext = createContext<BasketContextInterface>({} as BasketContextInterface);

interface BasketContextProviderInterface {
  children: React.ReactNode;
}

const BasketContextProvider: React.FC<BasketContextProviderInterface> = ({ children }) => {
  const { userDetails, countryCode, currentStoreCodes, alcoholExpiryDate } = useStaticData();
  const [isBasketLoading, setIsBasketLoading] = useState<boolean>(false);
  const [previewBasketItems, _setPreviewBasketItems] = useState<PreviewBasketItem[]>([]);
  const [isAlcoholProhibited, setIsAlcoholProhibited] = useState<boolean>(false);
  const [prohibitedDurationsLoading, setProhibitedDurationsLoading] = useState<boolean>(false);

  const updateBasketItem = useCallback(
    async (
      item: BasketLineItem,
      enablePartialQuantity?: boolean,
      analytics?: any,
      isFromBasket?: boolean,
    ): Promise<void> => {
      item.quantity = enablePartialQuantity ? item.quantity : roundOffDecimalQuantity(item.quantity);

      if (!userDetails?.token) {
        throw new Error('User not authenticated');
      }

      const token = String(userDetails.token);
      let updateBasketItemResponse;
      try {
        setIsBasketLoading(true);

        updateBasketItemResponse = await BasketService.updateBasketItemPreview(
          [
            {
              productId: getProductIdWithoutPrefix(item.productId),
              quantity: item.quantity,
            },
          ],
          token,
          countryCode as COUNTRY,
        );

        if (updateBasketItemResponse?.data?.data?.basketItems) {
          if (!previewBasketItems?.length) {
            SendBrowserEvent.triggerAddToCartAnalytics(
              ANALYTICS.BASKET_CREATED,
              {
                ...(analytics?.pageName && {
                  pageName: analytics?.pageName,
                }),
              },
              isFromBasket,
            );
          }
          SendBrowserEvent.updateBasket(
            {
              productId: item?.productId,
              quantity: item?.quantity,
              idToken: token,
              variantId: item?.variantId,
            },
            isFromBasket,
          );
          _setPreviewBasketItems(updateBasketItemResponse.data.data.basketItems);
        }

        if (updateBasketItemResponse?.error) {
          throw updateBasketItemResponse.error;
        }
      } catch (error: any) {
        console.error('BasketContext: Error updating basket item:', error);
        throw new Error(error.description || error.message);
      } finally {
        setIsBasketLoading(false);
      }
    },
    [previewBasketItems, _setPreviewBasketItems, userDetails, countryCode],
  );

  const refreshBasket = useCallback(async (): Promise<void> => {
    if (!userDetails?.token) {
      console.info('No user token, skipping basket refresh');
      return;
    }

    const token = String(userDetails.token);
    try {
      setIsBasketLoading(true);
      const basketResponse = await BasketService.getBasketPreview(token, countryCode as COUNTRY);

      if (basketResponse?.data?.data?.basketItems) {
        _setPreviewBasketItems(basketResponse.data.data.basketItems);
      }
    } catch (error: any) {
      console.error('Error refreshing basket:', error);
    } finally {
      setIsBasketLoading(false);
    }
  }, [userDetails?.token, _setPreviewBasketItems, countryCode]);

  const getBasketItemQuantity = useCallback(
    (productId: string): number => {
      if (!previewBasketItems || !Array.isArray(previewBasketItems)) return 0;
      const item = previewBasketItems.find((item) => String(item.id) === productId);
      return item?.quantitySetByUser || 0;
    },
    [previewBasketItems],
  );

  useEffect(() => {
    const isInIframe = window && window.self !== window.top;
    const receiver = checkPlatform() === 'android' && !isInIframe ? document : window;

    const handleBasketMessage = (event: MessageEvent) => {
      try {
        let data;
        try {
          data = JSON.parse(event.data);
          if (!data) return;
        } catch {
          if (event?.data?.type) {
            data = event.data;
          } else return;
        }
        const { type, message } = data;
        if (type === WEBVIEW_MESSAGE_TYPE.basketItems && message) {
          const basketItems = typeof message === 'string' ? JSON.parse(message) : message;
          _setPreviewBasketItems(Array.isArray(basketItems) ? basketItems : []);
        } else if (type === WEBVIEW_MESSAGE_TYPE.REFRESH_BASKET) {
          if (message) {
            const basketItems = typeof message === 'string' ? JSON.parse(message) : message;
            _setPreviewBasketItems(Array.isArray(basketItems) ? basketItems : []);
          } else {
            void refreshBasket();
          }
        }
      } catch (error) {
        console.error('BasketContext: Error handling basket message', error);
      }
    };

    receiver.addEventListener('message', handleBasketMessage as EventListener);
    return () => {
      receiver.removeEventListener('message', handleBasketMessage as EventListener);
    };
  }, [refreshBasket]);

  const setAlcoholProhibited = async (isAvailable: boolean) => {
    const storeCode = currentStoreCodes?.[0];
    const accessToken = String(userDetails?.token);
    setProhibitedDurationsLoading(true);

    if (!storeCode || !accessToken || !isAvailable) {
      setProhibitedDurationsLoading(false);
      return;
    }
    try {
      const prohibitedDurationData = await BasketService.getAlcoholProhibitedDurations({
        storeCode,
        accessToken,
      });

      const prohibitedDurations = prohibitedDurationData.map(({ startTime, endTime }) => ({
        startTime,
        endTime,
      }));

      if (prohibitedDurations.length) {
        if (countryCode === COUNTRY.CAMBODIA) {
          setIsAlcoholProhibited(false);
          return;
        }
        const isAlcoholProhibited = isAlcoholProhibitedPeriod([...prohibitedDurations]);
        setIsAlcoholProhibited(isAlcoholProhibited);
      }
    } catch (error) {
      console.error('Failed to fetch alcohol prohibited durations:', error);
    } finally {
      setProhibitedDurationsLoading(false);
    }
  };

  useEffect(() => {
    const expDate = alcoholExpiryDate ? new Date(alcoholExpiryDate.toString()) : null;
    const accessToken = userDetails?.token ? String(userDetails.token) : '';
    const isAvailable = !!expDate && expDate > new Date();

    if (currentStoreCodes?.length && accessToken) {
      setAlcoholProhibited(isAvailable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoreCodes, userDetails?.token, alcoholExpiryDate]);

  const values = {
    updateBasketItem,
    refreshBasket,
    getBasketItemQuantity,
    _setPreviewBasketItems,
    previewBasketItems,
    isBasketLoading,
    isAlcoholProhibited,
    prohibitedDurationsLoading,
  } as BasketContextInterface;

  return <BasketContext.Provider value={values}>{children}</BasketContext.Provider>;
};

export { BasketContext, BasketContextProvider };
